/// <reference types="vite/client" />
import { reactive } from 'vue';
import * as Sentry from '@sentry/vue';
import {
  PublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

export type MsalProps = {
  instance: PublicClientApplication,
  account?: AccountInfo,
  idToken?: string,
  refreshTimeout?: NodeJS.Timeout,
  isAuthenticating?: boolean,
  isRefreshing?: boolean,
};

export type $MsalProps = {
  signOut(): void;
  signIn(): Promise<void>;
  refreshToken(): Promise<void>;
  ensureAuthenticated(): Promise<void>;
};

const SPECIAL_USERS = [
  'laura.parucha@arkadium.com',
  'vitalii.ozerov@arkadium.com',
  'georgiy@arkadium.com',
  'georgiyn@arkadium.com',
  'alexander.djakov@arkadium.com',
  'andrey.cheblukov@arkadium.com',
  'filipe.fernandes@arkadium.com',
  'dor@arkadium.com',
  'natalia.marzan@arkadium.com',
  'oleg@arkadium.com',
  'a.konstantinov@arkadium.com',
  'robert.fieraru@arkadium.com',
  'aliaksei.lesik@arkadium.com',
  'ana.mota@arkadium.com'
];

const DEFAULT_REQUEST = { scopes: ['user.read'] };

type SetAccountParams = { account: AccountInfo; idToken?: string };

const isE2ETest = typeof import.meta !== 'undefined' && import.meta.env?.VITE_E2E === 'true';

export const msalPlugin = {
  install: async (Vue: any, config: any) => {
    if (isE2ETest) {
      const mockAccount = {
        homeAccountId: 'e2e',
        environment: 'e2e',
        tenantId: 'e2e',
        username: 'e2e-tests@arkadium.com',
        localAccountId: 'e2e',
        idTokenClaims: {
          roles: ['Admin'],
          exp: Math.floor(Date.now() / 1000) + 3600
        }
      } as AccountInfo;

      const msal = reactive<MsalProps>({
        instance: {} as PublicClientApplication,
        account: mockAccount,
        idToken: 'e2e-token',
        isAuthenticating: false,
        isRefreshing: false
      });

      const noop = async () => Promise.resolve();
      const $msal = reactive<$MsalProps>({
        signOut() { /* noop for e2e */ },
        async signIn() { return noop(); },
        async refreshToken() { return noop(); },
        async ensureAuthenticated() { return noop(); }
      });

      Vue.prototype.msal = msal;
      Vue.prototype.$msal = $msal;
      Vue.prototype.$checkRoles = () => true;
      Vue.prototype.$isSpecialUser = () => false;
      if (Vue.http?.headers?.common) {
        Vue.http.headers.common['Authorization'] = `Bearer ${msal.idToken}`;
      }
      return;
    }

    console.log(`MSAL CONFIG: `, config);
    const msalInstance = new PublicClientApplication(config);

    const msal = reactive<MsalProps>({
      instance: msalInstance,
      idToken: '',
      isAuthenticating: false,
      isRefreshing: false
    });

    const $msal = reactive<$MsalProps>({
      signOut() {
        Sentry.setUser(null);
        if (msal.account) {
          msalInstance.logoutRedirect({ account: msal.account });
        }
      },
      async signIn() {
        if (msal.isAuthenticating) {
          console.log('Authentication already in progress');
          return;
        }

        msal.isAuthenticating = true;
        try {
          await msalInstance.initialize();
          try {
            const result = await msalInstance.ssoSilent(DEFAULT_REQUEST);
            setAccount({ account: result.account, idToken: result.idToken });
          } catch (e) {
            console.log('SSO silent failed, trying popup login');
            const result = await msalInstance.loginPopup(DEFAULT_REQUEST);
            setAccount({ account: result.account, idToken: result.idToken });
          }
        } catch (error) {
          console.error('Authentication failed:', error);
          throw error;
        } finally {
          msal.isAuthenticating = false;
        }
      },
      async refreshToken() {
        if (msal.isRefreshing) {
          console.log('Token refresh already in progress, skipping...');
          return;
        }

        if (!msal.account) {
          console.log('No account found, signing in...');
          return this.signIn();
        }

        // Check if token is expired
        const tokenExp = msal.account.idTokenClaims?.exp;
        if (tokenExp && tokenExp * 1000 <= Date.now()) {
          console.log('Token expired, forcing re-authentication');
          return this.signIn();
        }

        msal.isRefreshing = true;
        try {
          console.log('Attempting to refresh token silently...');
          const result = await msalInstance.acquireTokenSilent(DEFAULT_REQUEST);
          setAccount({ account: result.account, idToken: result.idToken });
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            console.log('Silent refresh failed, trying popup...');
            try {
              const result = await msalInstance.acquireTokenPopup(DEFAULT_REQUEST);
              setAccount({ account: result.account, idToken: result.idToken });
            } catch (popupError) {
              console.error('Popup refresh failed:', popupError);
              return this.signIn();
            }
          } else {
            console.error('Token refresh failed:', e);
            return this.signIn();
          }
        } finally {
          msal.isRefreshing = false;
        }
      },
      async ensureAuthenticated() {
        // Check if we have a valid account and token
        if (!msal.account || !msal.idToken) {
          console.log('No account or token found, signing in...');
          return this.signIn();
        }

        // Check if token is expired or will expire soon
        const tokenExp = msal.account.idTokenClaims?.exp;
        if (tokenExp && tokenExp * 1000 <= Date.now() + 300000) { // 5 minutes buffer
          console.log('Token expired or expiring soon, refreshing...');
          return this.refreshToken();
        }

        console.log('Authentication is valid');
        return Promise.resolve();
      }
    });

    const setAccount = ({ account, idToken: idTokenFromResult }: SetAccountParams) => {
      if (msal.refreshTimeout) {
        clearTimeout(msal.refreshTimeout);
      }

      const fromAccount = (account as { idToken?: string }).idToken;
      const raw =
        idTokenFromResult != null && String(idTokenFromResult).trim() !== ''
          ? String(idTokenFromResult)
          : fromAccount != null && String(fromAccount).trim() !== ''
            ? String(fromAccount)
            : '';
      msal.account = account;
      msal.idToken = raw;
      msalInstance.setActiveAccount(account);
      if (raw) {
        Vue.http.headers.common['Authorization'] = `Bearer ${raw}`;
      } else {
        delete Vue.http.headers.common['Authorization'];
      }
      Sentry.setUser({
        id: account.localAccountId,
        email: account.username,
        segment: ((account.idTokenClaims?.roles as string[] | undefined) ?? []).join(',')
      });

      // Calculate refresh time with a 5-minute buffer
      const tokenExp = account.idTokenClaims?.exp;
      if (tokenExp) {
        const tokenExpiresIn = tokenExp * 1000 - Date.now() - 300000; // 5 minutes buffer
        console.log(`Next token refresh in ${tokenExpiresIn / 1000} seconds.`);

        if (tokenExpiresIn > 0) {
          msal.refreshTimeout = setTimeout(async () => {
            try {
              await $msal.refreshToken();
            } catch (error) {
              console.error('Scheduled token refresh failed:', error);
            }
          }, tokenExpiresIn);
        } else {
          console.log('Token is expired or will expire soon, refreshing immediately');
          if (!msal.isRefreshing) {
            $msal.refreshToken().catch(error => {
              console.error('Immediate token refresh failed:', error);
            });
          }
        }
      }
    };

    Vue.prototype.msal = msal;
    Vue.prototype.$msal = $msal;
    Vue.prototype.$checkRoles = function (requiredRoles: string[]) {
      const roles = msal.account?.idTokenClaims?.roles || [];
      return requiredRoles.some(requiredRole => roles.includes(requiredRole));
    };
    Vue.prototype.$isSpecialUser = function () {
      return msal.account && SPECIAL_USERS.includes(msal.account.username.toLowerCase());
    };

    await msalInstance.initialize();
    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult?.account) {
      setAccount({
        account: redirectResult.account,
        idToken: redirectResult.idToken,
      });
      return;
    }

    const [account] = msalInstance.getAllAccounts();

    if (!account) {
      try {
        const result = await msalInstance.ssoSilent(DEFAULT_REQUEST);
        setAccount({ account: result.account, idToken: result.idToken });
      } catch (e) {
        console.log('No logged in account found, waiting for user auth...');
      }
      return;
    }

    try {
      const result = await msalInstance.acquireTokenSilent({
        ...DEFAULT_REQUEST,
        account,
      });
      setAccount({ account: result.account, idToken: result.idToken });
    } catch (e) {
      console.log('acquireTokenSilent for cached account failed, trying ssoSilent', e);
      try {
        const result = await msalInstance.ssoSilent(DEFAULT_REQUEST);
        setAccount({ account: result.account, idToken: result.idToken });
      } catch (e2) {
        console.log('No logged in account found, waiting for user auth...');
      }
    }
  }
};
